import {
  IssueAmountStructure,
  IssueCaseDoc,
  IssueCaseStatusCode,
  IssueProduct,
  IssueTaskCategory,
  IssueTaskCreatorType,
  IssueTaskDoc,
  IssueTaskStatus,
} from '@gooduncles/gu-app-schema';
import { message, notification, Tag } from 'antd';
import { FC, Fragment } from 'react';
import {
  issueCaseKindColor,
  issueCaseKindLabel,
  issueCaseKindTagColor,
  normalizeCaseKind,
} from 'src/constants/issue.constants';
import { useAuth } from 'src/stores/auth-context';

import { createIssueTask, updateIssueCase, updateIssueTask } from 'src/utils/firebase/firebase-short-cut';
import {
  convertProductsToNegative,
  getIssueTaskInitialData,
  syncInventoryIssueInfoWithProducts,
} from 'src/utils/issue-task-util';
import { getCategoryLabel } from 'src/utils/issue/issue-data-util';
import {
  calculateIssueAmount,
  getIssueTaskLabel,
  issueTaskOptions,
  onUpdateIssueCaseProductQuantity,
} from 'src/utils/issue/issue-util';

import { AddButton } from 'src/atomic-components/atoms/AddButton';
import IssuePanelDivider from 'src/atomic-components/atoms/Divider/IssuePanelDivider';
import IssueCardHeader from 'src/atomic-components/atoms/IssueCardItem/IssueCardHeader';
import IssueCaseHeaderButtons from 'src/atomic-components/atoms/IssueCardItem/IssueCaseHeaderButtons';
import { InfoItem } from 'src/atomic-components/atoms/IssueInfoItem';
import IssueCaseStatusSelector from 'src/atomic-components/atoms/IssueStatusSelector/IssueCaseStatusSelector';
import AssignedIssueItem from 'src/atomic-components/molecules/AssignedIssueItem';
import IssueAmount from 'src/atomic-components/molecules/IssueAmount/IssueAmount';
import IssueCaseInfoSection from 'src/atomic-components/molecules/IssueCaseCard/IssueCaseInfoSection';
import { IssueProductItem } from 'src/atomic-components/molecules/IssueProductItem/IssueProductItem';
import IssueProductsEditModal from 'src/atomic-components/molecules/IssueProductsEditModal/IssueProductsEditModal';

interface IssueCaseCardProps {
  issueCaseDoc: IssueCaseDoc;
  issueTasks: IssueTaskDoc[];
  editable: boolean;
  parentProducts?: { [productId: string]: number };
  onTitleClick?: () => void;
  onIssueTaskClick?: (issueCaseId: string, issueTaskId: string) => void;
  style?: React.CSSProperties;
}
const IssueCaseCard: FC<IssueCaseCardProps> = ({
  issueCaseDoc,
  parentProducts,
  issueTasks,
  onTitleClick,
  onIssueTaskClick,
  editable,
  style,
}) => {
  const { user } = useAuth();
  const caseKind = normalizeCaseKind(issueCaseDoc.caseKind);
  const selectableProducts: IssueProduct[] = issueCaseDoc.products ?? [];
  const hasIssueTasks = issueTasks && issueTasks.length > 0;
  const issueProducts = issueCaseDoc.products ?? [];
  const disabled = (issueTasks ?? []).some((task) =>
    Object.values(task.inventoryIssueInfo?.products ?? {}).some((product) => product.status !== 'pending'),
  );

  const onAddIssueTask = async (category: string) => {
    if (!user) {
      message.error('사용자 정보가 없습니다. 다시 로그인 해주세요.');
      return;
    }
    const base = getIssueTaskInitialData(issueCaseDoc, {
      creatorId: user._id,
      creatorType: user.role as IssueTaskCreatorType,
      category: category as IssueTaskCategory,
    });
    try {
      await createIssueTask(base);
      message.success('이슈 태스크가 생성되었습니다.');
    } catch (error) {
      console.error(error);
      notification.error({ message: '이슈 태스크 생성에 실패했습니다. 다시 시도해주세요.' });
    }
  };

  const onAmountUpdate = async (amount: IssueAmountStructure) => {
    await updateIssueCase(issueCaseDoc._id, { amount });
  };

  const onUpdateIssueTaskProducts = async (issueTaskDoc: IssueTaskDoc, products: IssueProduct[]) => {
    try {
      // REFUND 카테고리인 경우 products의 수량과 금액을 음수로 변환
      const processedProducts =
        issueTaskDoc.category === IssueTaskCategory.REFUND ? convertProductsToNegative(products) ?? [] : products;

      // 변환된 products로 amount 계산
      const amount = calculateIssueAmount(processedProducts);
      const updateData: Partial<IssueTaskDoc> = { products: processedProducts, amount };

      // PICK_UP Task인 경우 inventoryIssueInfo.products도 동기화
      if (issueTaskDoc.category === IssueTaskCategory.PICK_UP && issueTaskDoc.inventoryIssueInfo) {
        updateData.inventoryIssueInfo = syncInventoryIssueInfoWithProducts(
          issueTaskDoc.inventoryIssueInfo,
          processedProducts,
        );
      }

      await updateIssueTask(issueTaskDoc._id, updateData);
    } catch (error) {
      console.error(error);
      notification.error({ message: '이슈 테스크 상품 업데이트에 실패했습니다. 다시 시도해주세요.' });
    }
  };

  return (
    <div style={style}>
      <IssueCardHeader
        id={issueCaseDoc._id}
        title={getCategoryLabel(issueCaseDoc.category)}
        spanProps={{ style: { color: issueCaseKindColor[caseKind] } }}
        onTitleClick={onTitleClick}
        badge={
          caseKind === 'request' ? (
            <Tag color={issueCaseKindTagColor.request} style={{ margin: 0 }}>
              {issueCaseKindLabel.request}
            </Tag>
          ) : undefined
        }
      >
        <IssueCaseHeaderButtons issueCaseDoc={issueCaseDoc} hasIssueTasks={hasIssueTasks} />
      </IssueCardHeader>
      <InfoItem
        label='상태'
        value={<IssueCaseStatusSelector issueCaseDoc={issueCaseDoc} issueTasks={issueTasks} disabled={disabled} />}
      />
      <IssueCaseInfoSection issueCaseDoc={issueCaseDoc} editable={editable} />
      <IssuePanelDivider />
      <InfoItem label='이슈 상품' value={issueProducts.length + '건'} />
      {issueProducts.length > 0
        ? issueProducts.map((product, index) => (
            <IssueProductItem
              key={index}
              editable={
                editable
                  ? {
                      maxQuantity: parentProducts?.[product.productId] ?? undefined,
                      onQuantityChange: (quantity) =>
                        onUpdateIssueCaseProductQuantity(issueCaseDoc, product.productId, quantity),
                    }
                  : undefined
              }
              productName={product.productName}
              unitPrice={product.price}
              quantity={product.quantity}
              totalPrice={product.price * product.quantity}
            />
          ))
        : null}
      <IssueAmount
        issueProducts={issueProducts}
        issueAmount={issueCaseDoc.amount}
        onAmountUpdate={editable ? onAmountUpdate : undefined}
      />
      <IssuePanelDivider />
      {hasIssueTasks && (
        <>
          {issueTasks.map((issueTask) => {
            const taskProducts = issueTask.products ?? [];
            return (
              <Fragment key={issueTask._id}>
                <AssignedIssueItem
                  label={getIssueTaskLabel(issueTask)}
                  color='blue'
                  onLabelClick={() => onIssueTaskClick?.(issueCaseDoc._id, issueTask._id)}
                  rightSection={
                    <IssueProductsEditModal
                      color='blue'
                      title={getIssueTaskLabel(issueTask)}
                      buttonText={issueTask.products ? `${issueTask.products.length}건` : '상품 등록'}
                      currentProducts={issueTask.products ?? []}
                      selectableProducts={selectableProducts}
                      editable={
                        editable &&
                        issueCaseDoc.status <= IssueCaseStatusCode.IN_PROGRESS &&
                        issueTask.status <= IssueTaskStatus.IN_PROGRESS
                      }
                      onUpdateProducts={(products) => onUpdateIssueTaskProducts(issueTask, products)}
                    />
                  }
                />
                {issueTask.products?.map((product, index) => (
                  <IssueProductItem
                    key={product.productId}
                    productName={product.productName}
                    unitPrice={product.price}
                    quantity={product.quantity}
                    totalPrice={product.price * product.quantity}
                    showHierarchy
                    isLast={index === taskProducts.length - 1}
                  />
                ))}
              </Fragment>
            );
          })}
          <IssuePanelDivider />
        </>
      )}
      {editable && (
        <AddButton text='새로운 이슈 태스크' color='blue' onSelect={onAddIssueTask} options={issueTaskOptions} />
      )}
    </div>
  );
};

export default IssueCaseCard;
export type { IssueCaseCardProps };
